import { styled } from '@mui/material/styles'
import Backdrop from '@mui/material/Backdrop'
import useStore from 'reusable/mobx-store-utils'

// Styled Matrix backdrop component
const MatrixBackdrop = styled(Backdrop)({
   backgroundColor: 'rgba(0, 0, 0, 0.95)',

   '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(0, 255, 0, 0.03) 2px,
        rgba(0, 255, 0, 0.03) 4px
      )
    `,
      pointerEvents: 'none',
   },

   '& .matrix-rain': {
      position: 'absolute',
      top: '0',
      fontSize: '16px',
      fontFamily: 'monospace',
      color: '#0f0',
      textShadow: '0 0 5px #0f0',
      animation: 'fall linear infinite',
      pointerEvents: 'none',
      lineHeight: '1.1',
      whiteSpace: 'pre',
   },

   '@keyframes fall': {
      '0%': {
         transform: 'translateY(-100%)',
         opacity: 1,
      },
      '100%': {
         transform: 'translateY(100vh)',
         opacity: 1,
      },
   },

   '& .col-1': { left: '2%', animationDuration: '6s', animationDelay: '0s' },
   '& .col-2': { left: '5%', animationDuration: '7s', animationDelay: '0.2s' },
   '& .col-3': { left: '8%', animationDuration: '6.5s', animationDelay: '0.4s' },
   '& .col-4': { left: '11%', animationDuration: '8s', animationDelay: '0.6s' },
   '& .col-5': { left: '14%', animationDuration: '6s', animationDelay: '0.8s' },
   '& .col-6': { left: '17%', animationDuration: '7.5s', animationDelay: '0.1s' },
   '& .col-7': { left: '20%', animationDuration: '6.5s', animationDelay: '0.3s' },
   '& .col-8': { left: '23%', animationDuration: '7s', animationDelay: '0.5s' },
   '& .col-9': { left: '26%', animationDuration: '8s', animationDelay: '0.7s' },
   '& .col-10': { left: '29%', animationDuration: '6s', animationDelay: '0.9s' },
   '& .col-11': { left: '32%', animationDuration: '7s', animationDelay: '0s' },
   '& .col-12': { left: '35%', animationDuration: '6.5s', animationDelay: '0.2s' },
   '& .col-13': { left: '38%', animationDuration: '7.5s', animationDelay: '0.4s' },
   '& .col-14': { left: '41%', animationDuration: '8s', animationDelay: '0.6s' },
   '& .col-15': { left: '44%', animationDuration: '6s', animationDelay: '0.8s' },
   '& .col-16': { left: '47%', animationDuration: '7s', animationDelay: '0.1s' },
   '& .col-17': { left: '50%', animationDuration: '6.5s', animationDelay: '0.3s' },
   '& .col-18': { left: '53%', animationDuration: '7.5s', animationDelay: '0.5s' },
   '& .col-19': { left: '56%', animationDuration: '6s', animationDelay: '0.7s' },
   '& .col-20': { left: '59%', animationDuration: '8s', animationDelay: '0.9s' },
   '& .col-21': { left: '62%', animationDuration: '7s', animationDelay: '0s' },
   '& .col-22': { left: '65%', animationDuration: '6.5s', animationDelay: '0.2s' },
   '& .col-23': { left: '68%', animationDuration: '7.5s', animationDelay: '0.4s' },
   '& .col-24': { left: '71%', animationDuration: '6s', animationDelay: '0.6s' },
   '& .col-25': { left: '74%', animationDuration: '8s', animationDelay: '0.8s' },
   '& .col-26': { left: '77%', animationDuration: '7s', animationDelay: '0.1s' },
   '& .col-27': { left: '80%', animationDuration: '6.5s', animationDelay: '0.3s' },
   '& .col-28': { left: '83%', animationDuration: '7.5s', animationDelay: '0.5s' },
   '& .col-29': { left: '86%', animationDuration: '6s', animationDelay: '0.7s' },
   '& .col-30': { left: '89%', animationDuration: '8s', animationDelay: '0.9s' },
   '& .col-31': { left: '92%', animationDuration: '7s', animationDelay: '0s' },
   '& .col-32': { left: '95%', animationDuration: '6.5s', animationDelay: '0.2s' },
   '& .col-33': { left: '98%', animationDuration: '7.5s', animationDelay: '0.4s' },
   '& .col-34': { left: '4%', animationDuration: '6s', animationDelay: '0.15s' },
   '& .col-35': { left: '7%', animationDuration: '8s', animationDelay: '0.35s' },
   '& .col-36': { left: '10%', animationDuration: '7s', animationDelay: '0.55s' },
   '& .col-37': { left: '13%', animationDuration: '6.5s', animationDelay: '0.75s' },
   '& .col-38': { left: '16%', animationDuration: '7.5s', animationDelay: '0.95s' },
   '& .col-39': { left: '19%', animationDuration: '6s', animationDelay: '0.05s' },
   '& .col-40': { left: '22%', animationDuration: '8s', animationDelay: '0.25s' },
})

// Generate random Matrix characters
const generateMatrixChars = () => {
   const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ'
   let result = ''
   for (let i = 0; i < 50; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length)) + '\n'
   }
   return result
}

const matrixColumns = Array.from({ length: 40 }, (_, i) => (
   <div
      key={i}
      className={`matrix-rain col-${i + 1}`}
   >
      {generateMatrixChars()}
   </div>
))

export default function useMatrix() {
   const matrixStore = useStore(() => ({
      enabled: localStorage.getItem('gap-matrix') === 'true',
      toggle: () => {
         const enabled = localStorage.getItem('gap-matrix') === 'true'
         if (enabled) {
            matrixStore.enabled = false
            localStorage.removeItem('gap-matrix')
            return false
         } else {
            matrixStore.enabled = true
            localStorage.setItem('gap-matrix', 'true')
            return true
         }
      }
   }))

   return {
      toggle: matrixStore.toggle,
      props: matrixStore.enabled ? {
         slots: {
            backdrop: MatrixBackdrop
         },
         slotProps: {
            backdrop: {
               children: matrixColumns
            }
         }
      } : {}
   }
}
